import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShieldCheck, PieChart, Mail, Phone } from 'lucide-react';

const InvestmentOpportunities = () => {

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  // Data
  const opportunities = [
    { type: "Multi-Family Development", range: "5M - 20M", roi: "18-25%" },
    { type: "Commercial Real Estate", range: "10M - 35M", roi: "20-28%" },
    { type: "Green Building Projects", range: "3M - 15M", roi: "22-30%" },
    { type: "Mixed-Use Development", range: "15M - 50M", roi: "24-32%" },
    { type: "International Expansion", range: "50M+", roi: "30-40%" },
  ];

  const advantages = [
    "Proven track record with 16+ years of successful project delivery",
    "Consistent financial growth from 1M to 18.7M in annual revenue",
    "Experienced management team with deep industry relationships",
    "Diversified portfolio across multiple high-growth markets",
    "Strong market position in high-demand Washington DC metropolitan area",
    "Transparent reporting and regular investor communications"
  ];

  return (
    <section className="bg-[#fffcf5] relative overflow-hidden text-[#0f172a]" style={{ fontFamily: 'Rubik, sans-serif' }}>

      <div className="max-w-full mx-auto relative z-10">

        {/* --- NEW TOP BANNER IMAGE --- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full h-72 md:h-[500px] overflow-hidden mb-16 relative shadow-2xl"
        >
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
            alt="Investment Opportunities"
            className="w-full h-full object-cover"
          />
          {/* Overlay to make text readable if you put text on image, or just for style */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/80 to-transparent flex items-end p-10">
            <div className="max-w-2xl">
              <span className="inline-block px-3 py-1 bg-[#9a8456] text-white text-xs font-bold tracking-widest uppercase rounded-sm mb-3">
                Strategic Growth
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-white">
                Building Wealth Through Real Estate
              </h3>
            </div>
          </div>
        </motion.div>

        {/* --- HEADER --- */}
        <div className="max-w-3xl mb-12 pl-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 mb-4"
          >
            <TrendingUp className="text-[#9a8456]" size={24} />
            <span className="text-[#9a8456] font-bold tracking-widest uppercase text-sm">Grow With Us</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-semibold mb-6 text-[#0f172a]"
          >
            Investment <span className="text-[#2a3890]">Opportunities</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 text-lg leading-relaxed"
          >
            TM International Group offers select investment opportunities in construction and real estate development projects
            with attractive returns and strategic growth potential. Our proven track record makes us an attractive partner
            for institutional investors, family offices, and high-net-worth individuals.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pl-20 pr-20">

          {/* --- LEFT: OPPORTUNITIES CARDS --- */}
          <div className="lg:col-span-7">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {opportunities.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  // CHANGED: Card style for Light Theme (White bg, shadow, border)
                  className={`p-6 bg-white border border-gray-100 shadow-md hover:shadow-xl hover:border-[#9a8456] transition-all duration-300 border-l-4 border-l-[#2a3890] group ${index === opportunities.length - 1 ? 'md:col-span-2' : ''}`}
                >
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-[#0f172a] group-hover:text-[#2a3890] transition-colors">
                      {item.type}
                    </h3>
                  </div>

                  <div className="flex justify-between items-end border-t border-gray-100 pt-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Investment Range</p>
                      <p className="font-mono text-[#0f172a] font-semibold">{item.range}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Expected ROI</p>
                      <p className="text-2xl font-bold text-[#9a8456]">{item.roi}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* --- RIGHT: ADVANTAGES & CTA --- */}
          <div className="lg:col-span-5 space-y-8">

            {/* Advantages List */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              // CHANGED: White bg with stronger shadow for contrast
              className="bg-white border-l-4 border-l-[#9a8456] p-8 text-[#0f172a] shadow-lg border border-gray-100"
            >
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-[#0f172a]">
                <PieChart className="text-[#9a8456]" />
                Investment Advantages
              </h3>

              <ul className="space-y-4">
                {advantages.map((adv, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ShieldCheck className="text-[#2a3890] shrink-0 mt-1" size={18} />
                    <span className="text-gray-600 text-sm leading-relaxed font-medium">
                      {adv}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* CTA Box (Kept Gradient but adjusted shadow) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full max-w-md bg-gradient-to-br from-[#0f172a] to-[#2a3890] border-l-4 border-l-[#9a8456] p-6 sm:p-8 text-white text-center shadow-xl rounded-lg"
            >
              <h4 className="text-xl font-bold mb-4">Interested in Partnering?</h4>
              <p className="text-gray-300 text-sm mb-6">
                For detailed project proposals and investment documentation, contact our team.
              </p>

              <div className="flex flex-col gap-3">
                <a
                  href="https://mail.google.com/mail/?view=cm&to=investments@tmgroupdc.com" target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 p-3 transition-colors text-xs sm:text-sm font-semibold border border-white/10 rounded break-all"
                >
                  <Mail size={16} className="shrink-0" />
                  <a href="https://mail.google.com/mail/?view=cm&to=investments@tmgroupdc.com" target="_blank" rel="noopener noreferrer">info@tmgroupdc.com</a>
                </a>

                <a
                  href="tel:+18886454575"
                  className="flex items-center justify-center gap-2 bg-[#9a8456] hover:bg-[#857147] p-3 transition-colors text-sm font-semibold shadow-md text-white rounded"
                >
                  <Phone size={16} className="shrink-0" />
                  <span>+1 (202) 445 4459</span>
                </a>
              </div>
            </motion.div>
          </div>

        </div>
      </div>

      <div className="bg-gray-900 max-w-full mx-auto px-4 text-center mt-10 py-12 text-white">
        <h2 className="text-2xl font-display uppercase tracking-widest mb-4">Ready to build your vision?</h2>
        <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition uppercase text-sm tracking-wider"
          onClick={() => window.location.href = '/contact'}>
          Contact Us
        </button>
      </div>

    </section>
  );
};

export default InvestmentOpportunities;